import { ResponsiveLine } from '@nivo/line';
import constants from '../../../styles/contants.module.scss';
import { useCurrentUser } from '../../../providers/UserProvider';
import styles from './IncidentHistoricalStatistics.module.scss';
import TextWithIcon from '../../../components/typography/TextWithIcon';
import { Text } from '../../../components/typography';
import { Form } from 'react-bootstrap';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useCompanyMutations } from '../../../hooks/company/update_company';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  computeRollingAll,
  fromIdx,
  getMonthKey,
  isFilled,
  monthLabel,
  rollingKeysForMonth,
  toIdx,
  toNum,
} from '../../../utilities/kpis';
import Icon from '../../../components/Icon';

const now = new Date();
const RATE_MULTIPLIER = 200000;

function getTrailingWindowEndKey(viewYear) {
  if (viewYear === now.getFullYear()) {
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return getMonthKey(prev.getFullYear(), prev.getMonth());
  }
  return getMonthKey(viewYear, 11);
}

export default function IncidentHistoricalStatGraph({
  year = new Date().getFullYear(),
  setYear = () => {},
  readOnly = false,
}) {
  const { company } = useCurrentUser();
  const workingHours = useMemo(
    () => company.workingHours || {},
    [company.workingHours],
  );

  const currentIdx = now.getFullYear() * 12 + now.getMonth();
  const { updateCompany } = useCompanyMutations();
  const { enableTrifTrends } = useFlags();

  const initialEndKey = useMemo(() => getTrailingWindowEndKey(year), [year]);
  const [endKey, setEndKey] = useState(initialEndKey);

  useEffect(() => {
    setEndKey(getTrailingWindowEndKey(year));
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const trailingMonths = useMemo(() => {
    const endIdx = toIdx(endKey);
    const startIdx = endIdx - 11;
    return Array.from({ length: 12 }, (_, i) => fromIdx(startIdx + i));
  }, [endKey]);

  const [formattedData, setData] = useState({});
  const handleChange = (key, field, value) => {
    setData((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const getInputValue = useCallback(
    (key, field) => {
      const typed = formattedData[key]?.[field];
      if (typed !== undefined) return typed;
      const saved = workingHours?.[key]?.[field];
      return isFilled(saved) ? String(saved) : '';
    },
    [formattedData, workingHours],
  );

  const getWorkedForKey = useCallback(
    (key) => {
      const typed = formattedData?.[key]?.worked;
      if (isFilled(typed)) return toNum(typed);
      const saved = workingHours?.[key]?.worked;
      if (isFilled(saved)) return toNum(saved);
      return 0;
    },
    [formattedData, workingHours],
  );

  const getLostForKey = useCallback(
    (key) => {
      const typed = formattedData?.[key]?.lost;
      if (isFilled(typed)) return toNum(typed);
      const saved = workingHours?.[key]?.lost;
      if (isFilled(saved)) return toNum(saved);
      return 0;
    },
    [formattedData, workingHours],
  );

  const getLtiForKey = useCallback(
    (key) => {
      const typed = formattedData?.[key]?.lti;
      if (isFilled(typed)) return toNum(typed);
      const saved = workingHours?.[key]?.lti;
      if (isFilled(saved)) return toNum(saved);
      return 0;
    },
    [formattedData, workingHours],
  );

  const getRecordablesForKey = useCallback(
    (key) => {
      const typed = formattedData?.[key]?.recordables;
      if (isFilled(typed)) return toNum(typed);
      const saved = workingHours?.[key]?.recordables;
      if (isFilled(saved)) return toNum(saved);
      return 0;
    },
    [formattedData, workingHours],
  );

  const rollingWorked12ByKey = useMemo(() => {
    const m = {};
    trailingMonths.forEach((k) => {
      const keys = rollingKeysForMonth(k, 12);
      m[k] = keys.reduce((s, kk) => s + getWorkedForKey(kk), 0);
    });
    return m;
  }, [trailingMonths, getWorkedForKey]);

  const saveHoursWithRolling = useCallback(() => {
    const merged = { ...(workingHours || {}) };

    for (const [k, v] of Object.entries(formattedData)) {
      const out = { ...(merged[k] || {}) };
      if (v.worked !== undefined) out.worked = toNum(v.worked);
      if (v.lost !== undefined) out.lost = toNum(v.lost);
      if (v.lti !== undefined) out.lti = toNum(v.lti);
      if (v.recordables !== undefined) out.recordables = toNum(v.recordables);
      merged[k] = out;
    }

    const withRolling = computeRollingAll(merged);

    for (const [k, base] of Object.entries(merged)) {
      withRolling[k] = { ...(withRolling[k] || {}), ...base };
    }

    updateCompany({ workingHours: withRolling });
  }, [formattedData, workingHours, updateCompany]);

  const endIdx = toIdx(endKey);
  const minEndIdx = -Infinity;
  const canGoLeft = endIdx > minEndIdx;
  const canGoRight = endIdx < currentIdx;

  const goLeft = () => {
    if (canGoLeft) setEndKey(fromIdx(endIdx - 1));
  };
  const goRight = () => {
    if (canGoRight) setEndKey(fromIdx(endIdx + 1));
  };
  const goToNow = () => {
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setYear?.(prev.getFullYear());
    setEndKey(getMonthKey(prev.getFullYear(), prev.getMonth()));
  };

  const xKeys = trailingMonths;

  const buildRateSeries = (monthlyValueFn, multiplier = RATE_MULTIPLIER) => {
    return xKeys.map((mk) => {
      const keys = rollingKeysForMonth(mk, 12);
      const numerator = keys.reduce((s, k) => s + monthlyValueFn(k), 0);
      const denomWorked = keys.reduce((s, k) => s + getWorkedForKey(k), 0);

      const yVal =
        denomWorked > 0
          ? Number(((numerator * multiplier) / denomWorked).toFixed(2))
          : null;

      return {
        x: mk,
        y: yVal,
        xFormatted: mk,
        yFormatted: yVal != null ? yVal.toFixed(2) : 'No data',
      };
    });
  };

  const lines = [
    {
      id: 'TRIF',
      data: buildRateSeries((k) => getRecordablesForKey(k), 200000),
    },
    { id: 'LTIR', data: buildRateSeries((k) => getLtiForKey(k), 1000000) },

    {
      id: 'Recordables',
      data: xKeys.map((mk) => ({
        x: mk,
        y: getRecordablesForKey(mk),
        xFormatted: mk,
        yFormatted: String(getRecordablesForKey(mk)),
      })),
    },
    {
      id: 'Lost Time Incidents',
      data: xKeys.map((mk) => ({
        x: mk,
        y: getLtiForKey(mk),
        xFormatted: mk,
        yFormatted: String(getLtiForKey(mk)),
      })),
    },
    {
      id: 'Lost Time Hrs',
      data: xKeys.map((mk) => ({
        x: mk,
        y: getLostForKey(mk),
        xFormatted: mk,
        yFormatted: String(getLostForKey(mk)),
      })),
    },
  ];

  const rangeLabel = `${monthLabel(xKeys[0])} – ${monthLabel(
    xKeys[xKeys.length - 1],
  )}`;

  const goToFirstNoHours = useCallback(() => {
    const keys = Object.keys(workingHours || {}).filter((k) =>
      /^\d{4}-\d{2}$/.test(k),
    );
    const lastCompleteIdx = currentIdx - 1;

    let startIdx = null;
    if (keys.length) {
      startIdx = Math.min(...keys.map((k) => toIdx(k)));
    } else {
      const endIdxLocal = toIdx(endKey);
      startIdx = endIdxLocal - 11;
    }

    for (let idx = startIdx; idx <= lastCompleteIdx; idx += 1) {
      const key = fromIdx(idx);
      const savedWorked = workingHours?.[key]?.worked;
      if (!isFilled(savedWorked)) {
        setEndKey(key);
        setYear?.(Number(key.slice(0, 4)));
        return;
      }
    }

    const fallbackKey = fromIdx(startIdx);
    setEndKey(fallbackKey);
    setYear?.(Number(fallbackKey.slice(0, 4)));
  }, [workingHours, currentIdx, endKey, setYear]);

  if (!enableTrifTrends) return null;

  return (
    <div className={styles.page}>
      <Text marginSm weight="semiBold">
        Rolling 12-Month Safety Performance
      </Text>
      <div className={styles.container}>
        <div className={constants.spaceBetween}>
          <div style={{ height: 400, width: '100%' }}>
            <div
              className={constants.flexRowSm}
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Icon hover disabled={!canGoRight} onClick={goToFirstNoHours}>
                first_page
              </Icon>
              <TextWithIcon
                disableLeftIcon={!canGoLeft}
                disableRightIcon={!canGoRight}
                rightHover={canGoRight}
                leftHover={canGoLeft}
                leftIcon="arrow_left"
                icon="arrow_right"
                weight="bold"
                size="md-lg"
                onLeftIconClick={goLeft}
                onRightIconClick={goRight}
              >
                {rangeLabel}
              </TextWithIcon>
              <Icon hover disabled={!canGoRight} onClick={goToNow}>
                last_page
              </Icon>
            </div>
            <ResponsiveLine
              data={lines}
              margin={{ top: 30, right: 140, bottom: 80, left: 60 }}
              xScale={{ type: 'point' }}
              useMesh={true}
              yScale={{
                type: 'linear',
                min: 'auto',
                max: 'auto',
                stacked: false,
              }}
              axisBottom={{
                orient: 'bottom',
                legendOffset: 36,
                legendPosition: 'middle',
                tickRotation: -45,
              }}
              axisLeft={{
                orient: 'left',
                legend: 'Value',
                legendOffset: -40,
                legendPosition: 'middle',
              }}
              pointSize={8}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              colors={{ scheme: 'category10' }}
              enableSlices="x"
              legends={[
                {
                  anchor: 'bottom-right',
                  direction: 'column',
                  translateX: 160,
                  itemsSpacing: 6,
                  itemWidth: 140,
                  itemHeight: 20,
                  itemOpacity: 0.75,
                  symbolSize: 14,
                  symbolShape: 'circle',
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemBackground: 'rgba(0,0,0,.03)',
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]}
              xFormat={(v) => monthLabel(v)}
            />
          </div>
        </div>
        <Text weight="semiBold" noMargin>
          Monthly Working Hours
        </Text>
        <div className={styles.inputs}>
          <div className={styles.flexRowSm}>
            {[
              'Month',
              'Working Hrs',
              'Lost Time Hrs',
              'Lost Time Incidents',
              'Recordables',
              'Total Hours',
            ].map((h) => (
              <div key={h} className={styles.cell}>
                <Text textAlign="center" size="sm" weight="semiBold" noMargin>
                  {h}
                </Text>
              </div>
            ))}
          </div>
          {trailingMonths.map((key) => (
            <div className={styles.flexRowSm} key={key}>
              <div className={styles.cell}>
                <Text textAlign="center" noMargin weight="bold" size="sm">
                  {key}
                </Text>
              </div>
              <div className={styles.cell}>
                {readOnly ? (
                  <Text noMargin textAlign="center" size="sm">
                    {getInputValue(key, 'worked') || 0}
                  </Text>
                ) : (
                  <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    className={styles.input}
                    placeholder="0"
                    value={getInputValue(key, 'worked')}
                    onChange={(e) =>
                      handleChange(key, 'worked', e.target.value)
                    }
                    onBlur={saveHoursWithRolling}
                  />
                )}
              </div>
              <div className={styles.cell}>
                {readOnly ? (
                  <Text noMargin textAlign="center" size="sm">
                    {getInputValue(key, 'lost') || 0}
                  </Text>
                ) : (
                  <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    placeholder="0"
                    className={styles.input}
                    value={getInputValue(key, 'lost')}
                    onChange={(e) => handleChange(key, 'lost', e.target.value)}
                    onBlur={saveHoursWithRolling}
                  />
                )}
              </div>
              <div className={styles.cell}>
                {readOnly ? (
                  <Text noMargin textAlign="center" size="sm">
                    {getInputValue(key, 'lti') || 0}
                  </Text>
                ) : (
                  <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    className={styles.input}
                    placeholder="0"
                    value={getInputValue(key, 'lti')}
                    onChange={(e) => handleChange(key, 'lti', e.target.value)}
                    onBlur={saveHoursWithRolling}
                  />
                )}
              </div>

              <div className={styles.cell}>
                {readOnly ? (
                  <Text noMargin textAlign="center" size="sm">
                    {getInputValue(key, 'recordables') || 0}
                  </Text>
                ) : (
                  <Form.Control
                    size="sm"
                    type="number"
                    min={0}
                    className={styles.input}
                    placeholder="0"
                    value={getInputValue(key, 'recordables')}
                    onChange={(e) =>
                      handleChange(key, 'recordables', e.target.value)
                    }
                    onBlur={saveHoursWithRolling}
                  />
                )}
              </div>
              <div className={styles.cell}>
                <Text textAlign="center" size="sm" noMargin>
                  {toNum(rollingWorked12ByKey[key] ?? 0).toLocaleString()}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
